import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { Auth } from 'aws-amplify';

const PAYMENT_API_BASE = 'https://api.gogamify.com/payment';

interface StripeCustomer {
  id: string;
  email: string;
  name: string | null;
}

interface StripeCustomerModalProps {
  open: boolean;
  onClose: () => void;
  orgID: string;
  onCustomerLinked: (stripeCustomerID: string, billingType?: string) => void;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const session = await Auth.currentSession();
  const token = session.getIdToken().getJwtToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export default function StripeCustomerModal({
  open,
  onClose,
  orgID,
  onCustomerLinked,
}: StripeCustomerModalProps) {
  const [email, setEmail] = useState('');
  const [customers, setCustomers] = useState<StripeCustomer[]>([]);
  const [selectedCustomer, setSelectedCustomer] =
    useState<StripeCustomer | null>(null);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [linkError, setLinkError] = useState('');
  const [conflictOrg, setConflictOrg] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const resetState = () => {
    setEmail('');
    setCustomers([]);
    setSelectedCustomer(null);
    setSearching(false);
    setLinking(false);
    setSearchError('');
    setLinkError('');
    setConflictOrg('');
    setHasSearched(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSearch = async () => {
    if (!email.trim()) return;

    setSearching(true);
    setSearchError('');
    setCustomers([]);
    setSelectedCustomer(null);
    setLinkError('');
    setConflictOrg('');
    setHasSearched(true);

    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${PAYMENT_API_BASE}/search-stripe-customers`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();

      if (data.success && data.data?.customers) {
        setCustomers(data.data.customers);
      } else {
        setSearchError(data.error || 'Failed to search customers');
      }
    } catch (err) {
      setSearchError('Network error searching Stripe customers');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectCustomer = (customer: StripeCustomer) => {
    setSelectedCustomer(customer);
    setLinkError('');
    setConflictOrg('');
  };

  const handleLink = async () => {
    if (!selectedCustomer) return;

    setLinking(true);
    setLinkError('');
    setConflictOrg('');

    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${PAYMENT_API_BASE}/link-stripe-customer`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          orgID,
          stripeCustomerID: selectedCustomer.id,
        }),
      });
      const data = await res.json();

      if (res.status === 409) {
        setConflictOrg(data.conflictingOrg || 'another organization');
        setLinkError(
          data.error || 'This Stripe customer is already linked to another org'
        );
        return;
      }

      if (data.success) {
        onCustomerLinked(selectedCustomer.id, data.data?.billingType);
        resetState();
      } else {
        setLinkError(data.error || 'Failed to link customer');
      }
    } catch (err) {
      setLinkError('Network error linking Stripe customer');
    } finally {
      setLinking(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link Stripe Customer</DialogTitle>
      <DialogContent>
        {/* Search field */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
          <TextField
            label="Search by email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            fullWidth
            size="small"
            disabled={searching}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={searching || !email.trim()}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {searching ? <CircularProgress size={20} /> : 'Search'}
          </Button>
        </Box>

        {/* Search error */}
        {searchError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {searchError}
          </Alert>
        )}

        {/* Conflict / link error */}
        {conflictOrg && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This Stripe customer is already linked to:{' '}
            <strong>{conflictOrg}</strong>
          </Alert>
        )}
        {linkError && !conflictOrg && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {linkError}
          </Alert>
        )}

        {/* Loading spinner */}
        {searching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Results list */}
        {!searching && customers.length > 0 && (
          <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
            {customers.map((customer) => (
              <ListItem key={customer.id} disablePadding>
                <ListItemButton
                  selected={selectedCustomer?.id === customer.id}
                  onClick={() => handleSelectCustomer(customer)}
                >
                  <ListItemText
                    primary={customer.email}
                    secondary={
                      <>
                        {customer.name && (
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.primary"
                          >
                            {customer.name}
                          </Typography>
                        )}
                        {customer.name && ' — '}
                        <Typography component="span" variant="body2">
                          {customer.id}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        {/* No results */}
        {!searching &&
          hasSearched &&
          customers.length === 0 &&
          !searchError && (
            <Typography
              color="text.secondary"
              sx={{ py: 2, textAlign: 'center' }}
            >
              No customers found for that email
            </Typography>
          )}

        {/* Selected customer details */}
        {selectedCustomer && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Selected Customer
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {selectedCustomer.email}
            </Typography>
            {selectedCustomer.name && (
              <Typography variant="body2">
                <strong>Name:</strong> {selectedCustomer.name}
              </Typography>
            )}
            <Typography variant="body2">
              <strong>Stripe ID:</strong> {selectedCustomer.id}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleLink}
          disabled={!selectedCustomer || linking || !!conflictOrg}
        >
          {linking ? <CircularProgress size={20} /> : 'Link Customer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
